import {isFilled} from '@prismicio/client'
import {useSearchParams} from 'next/navigation'
import {useCallback} from 'react'
import type {UseFormReturn} from 'react-hook-form'
import {useForm} from 'react-hook-form'
import {useToasts} from 'react-toast-notifications'
import {useCookie} from 'react-use'
import {HUBSPOT_PORTAL_ID} from 'src/common/constants'
import {EVENTS, getUtmParams, track} from 'src/common/helpers'
import {useTranslate} from 'src/common/i18n/useTranslate'
import {logger} from 'src/common/logger'

export type FormData = Record<string, string>

export type LegalConsentCommunication = {
  value: boolean
  subscriptionTypeId: number
  text: string
}

export type LegalConsentOptions = {
  consent: {
    consentToProcess: boolean
    text: string
    communications: LegalConsentCommunication[]
  }
}

type UseHubspotFormOptions = {
  formId: string | null | undefined
  pageUid: string
  trackingLocation: string
  onSuccess?: () => void
  defaultValues?: Partial<FormData>
  trackDataType: string
  salesforce_campaign_id?: string | null
  getLegalConsentOptions?: () => LegalConsentOptions
}

type UseHubspotFormReturn = {
  form: UseFormReturn<FormData>
  submit: (formData: FormData) => Promise<void>
  isSubmitSuccessful: boolean
  isSubmitting: boolean
}

export const useHubspotForm = ({
  formId,
  pageUid,
  trackingLocation,
  onSuccess,
  trackDataType,
  defaultValues = {
    billing_country: '',
    hs_persona: '',
  },
  salesforce_campaign_id,
  getLegalConsentOptions,
}: UseHubspotFormOptions): UseHubspotFormReturn => {
  const form = useForm<FormData>({
    shouldUseNativeValidation: true,
    defaultValues,
  })

  const {
    formState: {isSubmitSuccessful, isSubmitting},
    setError,
    clearErrors,
  } = form

  const [hutk] = useCookie('hubspotutk')
  const params = useSearchParams()
  const {addToast} = useToasts()
  const translate = useTranslate()

  const submit = useCallback(
    async (formData: FormData) => {
      clearErrors(['root', 'email'])

      if (!isFilled.keyText(formId)) {
        addToast(translate('leadForm.errors.configuration'), {appearance: 'error'})
        return
      }

      const url = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${formId}`

      const utmFields = getUtmParams(params)

      const fields = Object.entries(formData)
        .filter(([key, value]) => key !== 'checkbox' && value !== '' && value != null)
        .map(([name, value]) => ({
          name,
          value: String(value),
        }))

      if (isFilled.keyText(salesforce_campaign_id)) {
        fields.push({name: 'salesforce_campaign_id', value: salesforce_campaign_id})
      }

      const preparedData = {
        submittedAt: new Date().getTime(),
        fields: [...fields, ...utmFields],
        context: {
          pageName: pageUid,
          pageUri: typeof window === 'undefined' ? '' : window.location.href,
          ...(hutk?.length && {hutk}),
        },
        ...(getLegalConsentOptions && {legalConsentOptions: getLegalConsentOptions()}),
      }

      const showFailure = (message: string) => {
        addToast(message, {appearance: 'error'})
        setError('root', {type: 'manual', message: 'Submission failed'})
      }

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(preparedData),
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))

          const isBlockedEmail = errorData.errors?.some((err: {message?: string}) =>
            err.message?.includes("Error in 'fields.email'"),
          )

          if (isBlockedEmail) {
            const corporateEmailRequired = translate('leadForm.errors.corporateEmailRequired')
            setError('email', {type: 'server', message: corporateEmailRequired})
            return showFailure(corporateEmailRequired)
          }

          logger.error('HubSpot form submission failed', {
            context: 'event-hubspot-form',
            httpStatus: response.status,
            formId,
          })

          return showFailure(errorData.message || 'Failed to submit form')
        }

        track({
          event: EVENTS.FORM_SUBMIT,
          location: trackingLocation,
          dataType: trackDataType,
          mktgId: 'form-submission-button',
          pageName: pageUid,
        })

        onSuccess?.()
      } catch (error) {
        logger.error('HubSpot form submission failed', {context: 'event-hubspot-form', formId})
        showFailure(error instanceof Error ? error.message : translate('leadForm.errors.generic'))
      }
    },
    [
      formId,
      salesforce_campaign_id,
      params,
      pageUid,
      hutk,
      addToast,
      setError,
      clearErrors,
      onSuccess,
      trackingLocation,
      trackDataType,
      getLegalConsentOptions,
      translate,
    ],
  )

  return {
    form,
    submit,
    isSubmitSuccessful,
    isSubmitting,
  }
}
